import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/lib/logger';
import ErrorFallbackPage from './ErrorFallbackPage';

interface Props {
  children: ReactNode;
  routeName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Prevent error cascade from DOM manipulation issues
    if (error.message?.includes('insertBefore') || error.message?.includes('Node')) {
      logger.error('RouteErrorBoundary: DOM manipulation error detected', { message: error.message });
      // Return hasError: false to prevent showing error page for DOM issues
      return { hasError: false };
    }
    logger.error('RouteErrorBoundary: Route error detected', { message: error.message });
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { routeName } = this.props;
    logger.error('RouteErrorBoundary caught error', { 
      error: error.message,
      route: routeName,
      componentStack: errorInfo.componentStack 
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallbackPage 
          error={this.state.error}
          routeName={this.props.routeName}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}
